import { DialogOptions } from '../../../models/modal.model'
import {
  GenericNotificationCode,
  NotificationOptions,
  parseGenericNotification,
} from '../../../models/notification.model'
import { GoodsReturn } from '../models/goods-return.entity'

export type GoodsReturnNotificationCode =
  | GenericNotificationCode
  | 'ATTACHMENTS_FOUND'
  | 'GOODS_RETURN_CANCELED'
  | 'GOODS_RETURN_DELETED'
  | 'GOODS_RETURN_NOT_FOUND'
  | 'GOODS_RETURN_PROCESSED'
  | 'GOODS_RETURN_SAVED'
  | 'PRODUCT_ADDED'
  | 'PRODUCT_NOT_FOUND'
  | 'MISSING_DATE'
  | 'MULTIPLE_GOODS_RETURN_FOUND'
  | 'WRONG_STATUS'

export type GoodsReturnModalCode =
  | 'GOODS_RETURN_CANCEL'
  | 'GOODS_RETURN_DELETION'
  | 'REF_ORDER_TAKEN'
  | 'ROWS_ERROR'

/**
 * Parse goods return code to a notification
 * @param code - the goods return notification code
 * @returns the notification options
 */
export function parseGoodsReturnNotification(
  code?: GoodsReturnNotificationCode,
): NotificationOptions {
  switch (code) {
    case 'ATTACHMENTS_FOUND':
      return {
        message: 'Goods returns with attachments cannot be deleted',
        color: 'error',
      }
    case 'GOODS_RETURN_CANCELED':
      return {
        message: 'Goods return canceled successfully',
        color: 'success',
      }
    case 'GOODS_RETURN_DELETED':
      return {
        message: 'Goods return deleted successfully',
        color: 'success',
      }
    case 'GOODS_RETURN_NOT_FOUND':
      return {
        message: 'Goods return not found',
        color: 'error',
      }
    case 'GOODS_RETURN_PROCESSED':
      return {
        message: 'Goods return updated successfully',
        color: 'success',
      }
    case 'GOODS_RETURN_SAVED':
      return {
        message: 'Goods return saved successfully',
        color: 'success',
      }
    case 'PRODUCT_ADDED':
      return {
        message: 'Product added',
        color: 'success',
      }
    case 'PRODUCT_NOT_FOUND':
      return {
        message: 'Product not found',
        color: 'error',
      }
    case 'MISSING_DATE':
      return {
        message: 'Arrival date missing',
        color: 'error',
      }
    case 'MULTIPLE_GOODS_RETURN_FOUND':
      return {
        message: 'More than one linked goods-return found',
        color: 'error',
      }
    case 'WRONG_STATUS':
      return {
        message: 'Only goods return in RETURNING status can be deleted',
        color: 'error',
      }
    default:
      return parseGenericNotification(code)
  }
}

/**
 * Parse goods return code to an modal
 * @param code - the goods return modal code
 * @param goodsReturn - the goods return object
 * @returns the modal settings
 */
export function parseGoodsReturnModal(
  code: GoodsReturnModalCode,
  goodsReturn?: GoodsReturn,
): DialogOptions {
  switch (code) {
    case 'GOODS_RETURN_CANCEL': {
      return {
        title: 'Cancel goods return',
        message:
          'The goods return will be permanently cancelled and it will no longer be possible to process it or make any type of modification',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'GOODS_RETURN_DELETION': {
      const grName = goodsReturn?.header.returnNumber
      return {
        title: 'Delete goods return',
        message:
          'You are about to delete the goods return <strong>%s</strong>. Proceed with deletion?',
        messageParams: grName ? [grName] : undefined,
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'REF_ORDER_TAKEN': {
      return {
        message: 'Order already associated with a goods return',
        buttons: ['Cancel', 'Open', 'Continue'],
      }
    }
    case 'ROWS_ERROR': {
      return {
        title: 'Rows error',
        message: 'Rows with all quantities set to zero cannot be accepted',
      }
    }
    default:
      return {}
  }
}
